import { createClient } from "@supabase/supabase-js";
import { getRuntimeEnvironment } from "../local/runtimeEnvironment";
import { resilientSupabaseStorage } from "./authStorage";
import {
  isSupabaseConfigured,
  supabaseAnonKey,
  supabaseUrl,
} from "./config";

const WE_AUTH_STORAGE_KEY = "sb-mozzoapp-we-customer-auth-token";
const WE_LOCAL_ACCOUNT_KEY = "mozzoapp:we-pedidos-local-account";
const WE_LOCAL_SESSION_KEY = "mozzoapp:we-pedidos-local-session";
const WE_AUTH_UPDATED_EVENT = "mozzoapp:we-pedidos-auth-updated";
const WE_CUSTOMER_BACKGROUND_DEVICE_KEY =
  "mozzoapp:we-pedidos-background-device:v1";
const PUBLIC_WE_FUNCTION = "public-we-pedidos";
const WE_LOCAL_DEMO_ACCOUNT = Object.freeze({
  userId: "local-we-customer-demo",
  firstName: "Cliente",
  lastName: "Prueba",
  fullName: "Cliente Prueba",
  email: "cliente.prueba@we.local",
  phone: "3794123456",
  password: "Prueba1234",
});

const useLocalPreviewAuth =
  getRuntimeEnvironment() === "development" &&
  String(process.env.REACT_APP_WE_LIVE_AUTH || "").trim() !== "true";

export const weCustomerSupabase = isSupabaseConfigured
  ? createClient(supabaseUrl, supabaseAnonKey, {
      auth: {
        storageKey: WE_AUTH_STORAGE_KEY,
        storage: resilientSupabaseStorage,
        persistSession: true,
        autoRefreshToken: true,
        detectSessionInUrl: false,
        flowType: "pkce",
      },
    })
  : null;

const getStorage = () =>
  typeof window !== "undefined" ? window.localStorage : null;

const getCustomerBackgroundDeviceId = () => {
  const storage = getStorage();
  const stored = cleanText(
    storage?.getItem(WE_CUSTOMER_BACKGROUND_DEVICE_KEY),
    160
  );
  if (stored) return stored;
  const randomId =
    typeof window !== "undefined" &&
    typeof window.crypto?.randomUUID === "function"
      ? window.crypto.randomUUID()
      : `customer-${Date.now()}-${Math.random().toString(36).slice(2, 14)}`;
  try {
    storage?.setItem(WE_CUSTOMER_BACKGROUND_DEVICE_KEY, randomId);
  } catch {
    // El launcher puede conservar el identificador solo durante esta sesion.
  }
  return randomId;
};

const readStoredJson = (key) => {
  try {
    return JSON.parse(getStorage()?.getItem(key) || "null");
  } catch {
    return null;
  }
};

const writeStoredJson = (key, value) => {
  getStorage()?.setItem(key, JSON.stringify(value));
};

const dispatchAuthUpdated = () => {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent(WE_AUTH_UPDATED_EVENT));
  }
};

const cleanText = (value, maxLength = 180) =>
  String(value || "").replace(/\s+/g, " ").trim().slice(0, maxLength);

const normalizeEmail = (value) => cleanText(value, 180).toLowerCase();

export const normalizeWeCustomerPhone = (value) =>
  cleanText(value, 60).replace(/\D/g, "");

const splitFullName = (fullName) => {
  const parts = cleanText(fullName, 160).split(" ").filter(Boolean);
  return {
    firstName: parts.shift() || "",
    lastName: parts.join(" "),
  };
};

const hashLocalPassword = async (password) => {
  const value = String(password || "");
  if (typeof crypto !== "undefined" && crypto.subtle && typeof TextEncoder !== "undefined") {
    const digest = await crypto.subtle.digest(
      "SHA-256",
      new TextEncoder().encode(value)
    );
    return Array.from(new Uint8Array(digest))
      .map((byte) => byte.toString(16).padStart(2, "0"))
      .join("");
  }
  return `preview-${value.length}-${value.charCodeAt(0) || 0}`;
};

const normalizeAddress = (address = {}) => ({
  id: cleanText(address.id, 120),
  label: cleanText(address.label, 60) || "Casa",
  addressLine: cleanText(address.addressLine ?? address.address_line, 220),
  street: cleanText(address.street, 160),
  streetNumber: cleanText(address.streetNumber ?? address.street_number, 40),
  floor: cleanText(address.floor, 30),
  unit: cleanText(address.unit, 30),
  neighborhood: cleanText(address.neighborhood, 100),
  city: cleanText(address.city, 100),
  province: cleanText(address.province, 100),
  postalCode: cleanText(address.postalCode ?? address.postal_code, 30),
  reference: cleanText(address.reference, 400),
  latitude:
    address.latitude === null || address.latitude === undefined
      ? null
      : Number(address.latitude),
  longitude:
    address.longitude === null || address.longitude === undefined
      ? null
      : Number(address.longitude),
  isDefault: address.isDefault === true || address.is_default === true,
});

const normalizeProfile = (profile = {}, user = null, addresses = []) => ({
  userId: cleanText(profile.userId ?? profile.user_id ?? user?.id, 120),
  firstName: cleanText(profile.firstName ?? profile.first_name, 80),
  lastName: cleanText(profile.lastName ?? profile.last_name, 80),
  fullName:
    cleanText(profile.fullName ?? profile.full_name, 160) ||
    cleanText(
      `${profile.firstName ?? profile.first_name ?? ""} ${
        profile.lastName ?? profile.last_name ?? ""
      }`,
      160
    ),
  email: normalizeEmail(profile.email || user?.email),
  phone: normalizeWeCustomerPhone(
    profile.phone ?? profile.phone_e164 ?? profile.phone_normalized
  ),
  status: cleanText(profile.status, 30) || "active",
  addresses: addresses.map(normalizeAddress),
});

const buildLocalAuthState = () => {
  const account = readStoredJson(WE_LOCAL_ACCOUNT_KEY);
  const session = readStoredJson(WE_LOCAL_SESSION_KEY);
  if (!account || !session || account.email !== session.email) {
    return { session: null, profile: null, localPreview: true };
  }

  const user = { id: account.userId, email: account.email };
  return {
    session: {
      user,
      access_token: "",
      localPreview: true,
    },
    profile: normalizeProfile(account, user, account.addresses || []),
    localPreview: true,
  };
};

const ensureRemoteProfile = async (user) => {
  const metadata = user?.user_metadata || {};
  const names = splitFullName(metadata.full_name || "");
  const payload = {
    user_id: user.id,
    first_name: cleanText(metadata.first_name || names.firstName, 80),
    last_name: cleanText(metadata.last_name || names.lastName, 80) || null,
    email: normalizeEmail(user.email),
    phone_e164: normalizeWeCustomerPhone(metadata.phone) || null,
    accepted_terms_at: metadata.accepted_terms_at || null,
    updated_at: new Date().toISOString(),
  };
  const { data: existing, error: existingError } = await weCustomerSupabase
    .from("we_customers")
    .select("user_id")
    .eq("user_id", user.id)
    .maybeSingle();
  if (existingError) {
    throw new Error(
      existingError.message || "No pudimos revisar tu cuenta de We! pedidos."
    );
  }

  const profileRequest = existing
    ? weCustomerSupabase
        .from("we_customers")
        .update({
          first_name: payload.first_name,
          last_name: payload.last_name,
          email: payload.email,
          phone_e164: payload.phone_e164,
          accepted_terms_at: payload.accepted_terms_at,
          updated_at: payload.updated_at,
        })
        .eq("user_id", user.id)
    : weCustomerSupabase.from("we_customers").insert(payload);
  const { data, error } = await profileRequest
    .select("user_id, first_name, last_name, email, phone_e164, status")
    .single();

  if (error) {
    throw new Error(
      error.message || "No pudimos preparar tu cuenta de We! pedidos."
    );
  }

  return data;
};

const loadRemoteAuthState = async () => {
  if (!weCustomerSupabase) {
    return { session: null, profile: null, localPreview: false };
  }

  const { data, error } = await weCustomerSupabase.auth.getSession();
  if (error) throw new Error(error.message || "No pudimos recuperar tu sesión.");
  const session = data?.session || null;
  if (!session?.user) {
    return { session: null, profile: null, localPreview: false };
  }

  const profileRow = await ensureRemoteProfile(session.user);
  const { data: addresses, error: addressError } = await weCustomerSupabase
    .from("we_customer_addresses")
    .select(
      "id, label, address_line, street, street_number, floor, unit, neighborhood, city, province, postal_code, reference, latitude, longitude, is_default"
    )
    .eq("customer_user_id", session.user.id)
    .order("is_default", { ascending: false })
    .order("updated_at", { ascending: false });

  if (addressError) {
    throw new Error(addressError.message || "No pudimos recuperar tus direcciones.");
  }

  return {
    session,
    profile: normalizeProfile(profileRow, session.user, addresses || []),
    localPreview: false,
  };
};

export const getWeCustomerAuthState = async () =>
  useLocalPreviewAuth ? buildLocalAuthState() : loadRemoteAuthState();

export const subscribeToWeCustomerAuth = (listener) => {
  if (useLocalPreviewAuth) {
    const handler = () => listener();
    window.addEventListener(WE_AUTH_UPDATED_EVENT, handler);
    window.addEventListener("storage", handler);
    return () => {
      window.removeEventListener(WE_AUTH_UPDATED_EVENT, handler);
      window.removeEventListener("storage", handler);
    };
  }

  if (!weCustomerSupabase) return () => {};
  const { data } = weCustomerSupabase.auth.onAuthStateChange(() => listener());
  return () => data.subscription.unsubscribe();
};

export const subscribeToWeCustomerOrderChanges = (userId, listener) => {
  const safeUserId = String(userId || "").trim();

  if (useLocalPreviewAuth || !weCustomerSupabase || !safeUserId) {
    return () => {};
  }

  const channel = weCustomerSupabase
    .channel(`we-customer-orders:${safeUserId}:${Date.now()}`)
    .on(
      "postgres_changes",
      {
        event: "*",
        schema: "public",
        table: "we_customer_order_live_status",
        filter: `we_customer_user_id=eq.${safeUserId}`,
      },
      () => {
        if (typeof listener === "function") listener();
      }
    )
    .subscribe();

  return () => {
    void weCustomerSupabase.removeChannel(channel);
  };
};

export const signUpWeCustomer = async ({
  fullName,
  email,
  phone,
  password,
  redirectTo,
}) => {
  const safeEmail = normalizeEmail(email);
  const safePhone = normalizeWeCustomerPhone(phone);
  const safeName = cleanText(fullName, 160);
  const { firstName, lastName } = splitFullName(safeName);

  if (!firstName || !lastName) {
    throw new Error("Ingresa tu nombre y apellido.");
  }
  if (!/^\S+@\S+\.\S+$/.test(safeEmail)) {
    throw new Error("Ingresa un correo válido.");
  }
  if (safePhone.length < 8) {
    throw new Error("Ingresa un número de celular válido.");
  }
  if (String(password || "").length < 8) {
    throw new Error("La contraseña debe tener al menos 8 caracteres.");
  }

  if (useLocalPreviewAuth) {
    const existing = readStoredJson(WE_LOCAL_ACCOUNT_KEY);
    if (existing?.email === safeEmail) {
      throw new Error("Ese correo ya está registrado en la vista local.");
    }
    const account = {
      userId: `local-${Date.now().toString(36)}`,
      firstName,
      lastName,
      fullName: safeName,
      email: safeEmail,
      phone: safePhone,
      passwordHash: await hashLocalPassword(password),
      acceptedTermsAt: new Date().toISOString(),
      addresses: [],
    };
    writeStoredJson(WE_LOCAL_ACCOUNT_KEY, account);
    writeStoredJson(WE_LOCAL_SESSION_KEY, {
      email: safeEmail,
      createdAt: new Date().toISOString(),
    });
    dispatchAuthUpdated();
    return { session: buildLocalAuthState().session, requiresEmailConfirmation: false };
  }

  if (!weCustomerSupabase) {
    throw new Error("El registro de We! pedidos no está configurado.");
  }

  const acceptedTermsAt = new Date().toISOString();
  const { data, error } = await weCustomerSupabase.auth.signUp({
    email: safeEmail,
    password,
    options: {
      emailRedirectTo: redirectTo,
      data: {
        account_type: "we_customer",
        full_name: safeName,
        first_name: firstName,
        last_name: lastName,
        phone: safePhone,
        accepted_terms_at: acceptedTermsAt,
      },
    },
  });

  if (error) throw new Error(error.message || "No pudimos crear tu cuenta.");
  if (data?.session?.user) await ensureRemoteProfile(data.session.user);
  return {
    session: data?.session || null,
    requiresEmailConfirmation: !data?.session,
  };
};

export const signInWeCustomer = async ({ email, password }) => {
  const safeEmail = normalizeEmail(email);
  if (useLocalPreviewAuth) {
    let account = readStoredJson(WE_LOCAL_ACCOUNT_KEY);
    const isDemoAccess =
      safeEmail === WE_LOCAL_DEMO_ACCOUNT.email &&
      String(password || "") === WE_LOCAL_DEMO_ACCOUNT.password;

    if (isDemoAccess && account?.email !== safeEmail) {
      account = {
        userId: WE_LOCAL_DEMO_ACCOUNT.userId,
        firstName: WE_LOCAL_DEMO_ACCOUNT.firstName,
        lastName: WE_LOCAL_DEMO_ACCOUNT.lastName,
        fullName: WE_LOCAL_DEMO_ACCOUNT.fullName,
        email: WE_LOCAL_DEMO_ACCOUNT.email,
        phone: WE_LOCAL_DEMO_ACCOUNT.phone,
        passwordHash: await hashLocalPassword(WE_LOCAL_DEMO_ACCOUNT.password),
        acceptedTermsAt: new Date().toISOString(),
        addresses: [],
      };
      writeStoredJson(WE_LOCAL_ACCOUNT_KEY, account);
    }

    const passwordHash = await hashLocalPassword(password);
    if (
      !account ||
      account.email !== safeEmail ||
      account.passwordHash !== passwordHash
    ) {
      throw new Error("El correo o la contraseña no coinciden.");
    }
    writeStoredJson(WE_LOCAL_SESSION_KEY, {
      email: safeEmail,
      createdAt: new Date().toISOString(),
    });
    dispatchAuthUpdated();
    return buildLocalAuthState();
  }

  if (!weCustomerSupabase) {
    throw new Error("El acceso de We! pedidos no está configurado.");
  }
  const { data, error } = await weCustomerSupabase.auth.signInWithPassword({
    email: safeEmail,
    password,
  });
  if (error) throw new Error(error.message || "No pudimos iniciar sesión.");
  await ensureRemoteProfile(data.user);
  return loadRemoteAuthState();
};

const invokeCustomerBackgroundAction = async (action, accessToken) => {
  if (!isSupabaseConfigured || !weCustomerSupabase || !accessToken) {
    return null;
  }

  const controller = new AbortController();
  const timeoutId = window.setTimeout(() => controller.abort(), 12000);
  try {
    const response = await fetch(
      `${supabaseUrl}/functions/v1/${PUBLIC_WE_FUNCTION}`,
      {
        method: "POST",
        headers: {
          apikey: supabaseAnonKey,
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          action,
          deviceId: getCustomerBackgroundDeviceId(),
        }),
        signal: controller.signal,
        cache: "no-store",
      }
    );
    const data = await response.json().catch(() => null);
    if (!response.ok || !data || data.ok === false) {
      throw new Error(
        data?.error || data?.message || "No pudimos configurar las alertas."
      );
    }
    return data;
  } finally {
    window.clearTimeout(timeoutId);
  }
};

export const registerWeCustomerBackgroundDevice = async ({
  accessToken = "",
} = {}) => {
  if (useLocalPreviewAuth) return null;
  let token = accessToken;
  if (!token && weCustomerSupabase) {
    const { data } = await weCustomerSupabase.auth.getSession();
    token = data?.session?.access_token || "";
  }
  const response = await invokeCustomerBackgroundAction(
    "register_background_device",
    token
  );
  if (!response?.deviceToken) return null;
  return {
    endpoint: `${supabaseUrl}/functions/v1/${PUBLIC_WE_FUNCTION}`,
    anonKey: supabaseAnonKey,
    deviceToken: response.deviceToken,
    expiresAt: response.expiresAt,
  };
};

export const revokeWeCustomerBackgroundDevice = async ({
  accessToken = "",
} = {}) => {
  if (useLocalPreviewAuth) return { ok: true };
  let token = accessToken;
  if (!token && weCustomerSupabase) {
    const { data } = await weCustomerSupabase.auth.getSession();
    token = data?.session?.access_token || "";
  }
  if (!token) return { ok: true };
  return invokeCustomerBackgroundAction("revoke_background_device", token);
};

export const signOutWeCustomer = async () => {
  if (useLocalPreviewAuth) {
    getStorage()?.removeItem(WE_LOCAL_SESSION_KEY);
    dispatchAuthUpdated();
    return;
  }
  if (!weCustomerSupabase) return;
  await revokeWeCustomerBackgroundDevice().catch(() => null);
  try {
    window.MozzoNotifications?.stopCustomerBackground?.();
  } catch {
    // Un navegador comun no tiene el puente nativo del launcher.
  }
  const { error } = await weCustomerSupabase.auth.signOut();
  if (error) throw new Error(error.message || "No pudimos cerrar la sesión.");
};

export const requestWePasswordReset = async ({ email, redirectTo }) => {
  if (useLocalPreviewAuth) {
    const account = readStoredJson(WE_LOCAL_ACCOUNT_KEY);
    if (!account || account.email !== normalizeEmail(email)) {
      throw new Error("No encontramos ese correo en la vista local.");
    }
    return { localPreview: true };
  }
  if (!weCustomerSupabase) {
    throw new Error("La recuperación de contraseña no está configurada.");
  }
  const { error } = await weCustomerSupabase.auth.resetPasswordForEmail(
    normalizeEmail(email),
    { redirectTo }
  );
  if (error) throw new Error(error.message || "No pudimos enviar el correo.");
  return { localPreview: false };
};

export const updateWeCustomerPassword = async (password) => {
  if (String(password || "").length < 8) {
    throw new Error("La contraseña debe tener al menos 8 caracteres.");
  }
  if (useLocalPreviewAuth) {
    const account = readStoredJson(WE_LOCAL_ACCOUNT_KEY);
    if (!account) throw new Error("No encontramos tu cuenta local.");
    writeStoredJson(WE_LOCAL_ACCOUNT_KEY, {
      ...account,
      passwordHash: await hashLocalPassword(password),
    });
    return;
  }
  if (!weCustomerSupabase) {
    throw new Error("La recuperación de contraseña no está configurada.");
  }
  const { error } = await weCustomerSupabase.auth.updateUser({ password });
  if (error) {
    throw new Error(error.message || "No pudimos actualizar la contraseña.");
  }
};

export const completeWeAuthCallback = async (callbackUrl) => {
  if (useLocalPreviewAuth) return buildLocalAuthState();
  if (!weCustomerSupabase) {
    throw new Error("El acceso de We! pedidos no está configurado.");
  }
  const url = new URL(callbackUrl);
  const code = url.searchParams.get("code");
  if (!code) throw new Error("El enlace de confirmación no es válido.");
  const { error } = await weCustomerSupabase.auth.exchangeCodeForSession(code);
  if (error) throw new Error(error.message || "No pudimos confirmar tu cuenta.");
  return loadRemoteAuthState();
};

export const saveWeCustomerAddress = async (address) => {
  const normalized = normalizeAddress(address);
  if (!normalized.city && !normalized.addressLine && !normalized.street) {
    throw new Error("Ingresa una ciudad o dirección para continuar.");
  }

  if (useLocalPreviewAuth) {
    const account = readStoredJson(WE_LOCAL_ACCOUNT_KEY);
    if (!account) throw new Error("Primero crea tu cuenta de We! pedidos.");
    const addressId = normalized.id || `local-address-${Date.now().toString(36)}`;
    const nextAddress = { ...normalized, id: addressId, isDefault: true };
    const otherAddresses = (account.addresses || [])
      .filter((item) => item.id !== addressId)
      .map((item) => ({ ...item, isDefault: false }));
    writeStoredJson(WE_LOCAL_ACCOUNT_KEY, {
      ...account,
      addresses: [nextAddress, ...otherAddresses],
    });
    dispatchAuthUpdated();
    return nextAddress;
  }

  const state = await loadRemoteAuthState();
  const userId = state.session?.user?.id;
  if (!userId) throw new Error("Tu sesión venció. Vuelve a ingresar.");
  const payload = {
    customer_user_id: userId,
    label: normalized.label,
    address_line:
      normalized.addressLine ||
      cleanText(`${normalized.street} ${normalized.streetNumber}`, 220),
    street: normalized.street || null,
    street_number: normalized.streetNumber || null,
    floor: normalized.floor || null,
    unit: normalized.unit || null,
    neighborhood: normalized.neighborhood || null,
    city: normalized.city || null,
    province: normalized.province || null,
    postal_code: normalized.postalCode || null,
    reference: normalized.reference || null,
    latitude: Number.isFinite(normalized.latitude) ? normalized.latitude : null,
    longitude: Number.isFinite(normalized.longitude) ? normalized.longitude : null,
    is_default: true,
    updated_at: new Date().toISOString(),
  };

  let request = weCustomerSupabase.from("we_customer_addresses");
  request = normalized.id
    ? request.update(payload).eq("id", normalized.id)
    : request.insert(payload);
  const { data, error } = await request
    .select(
      "id, label, address_line, street, street_number, floor, unit, neighborhood, city, province, postal_code, reference, latitude, longitude, is_default"
    )
    .single();
  if (error) throw new Error(error.message || "No pudimos guardar la dirección.");

  await weCustomerSupabase
    .from("we_customer_addresses")
    .update({ is_default: false })
    .eq("customer_user_id", userId)
    .neq("id", data.id)
    .eq("is_default", true);

  return normalizeAddress(data);
};

export const getWeCustomerAccessToken = async () => {
  const state = await getWeCustomerAuthState();
  return state.session?.access_token || "";
};

export const isUsingLocalWeCustomerAuth = () => useLocalPreviewAuth;
